import React, { useState } from "react";

import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import { FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import SelectorDias from "../components/SelectorDias";
import { RNHHorarioRangoIns } from "services/services-mia/programa-salud/personal/RNHHorarioRangoIns";
import { diasAParams } from "../../consts";

/**
 * Alta de un rango: una franja —entrada y salida— y en qué días aplica.
 *
 * El horario padre no se elige: esta vista vive adentro del modal de uno, así que ya
 * viene resuelto y solo se muestra para confirmar sobre cuál se está operando.
 *
 * Los días van fuera de Formik, en estado propio: el `ToggleButtonGroup` maneja un
 * array y no un valor de campo, así que meterlo en el schema era pelearse con yup
 * para validar algo que se chequea en una línea.
 *
 * Los siete días viajan siempre, en "Y" o en "N": `diasAParams` traduce el array del
 * front a los campos sueltos que espera el servicio.
 */
export default function InsertModal({ horario, updateList, closeModal }) {
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const [inRequest, setInRequest] = useState(false);
    const [dias, setDias] = useState([]);

    /**
     * Un rango sin dias no aplica nunca, asi que no se puede guardar.
     *
     * Los dias viven fuera de Formik —el ToggleButtonGroup maneja un array, no un
     * valor de campo— asi que esta regla no entra en el schema de yup: se evalua
     * aca y apaga el submit.
     */
    const sinDias = dias.length === 0;

    const handleSubmit = async (values) => {
        // Red por si el submit llega igual: con el boton apagado no deberia, pero el
        // Enter dentro de un input tambien dispara el form.
        if (sinDias) return;

        setInRequest(true);
        const respuesta = await RNHHorarioRangoIns(credentials, {
            horarioId: horario?.id,
            entrada: values.entrada,
            salida: values.salida,
            ...diasAParams(dias),
        });
        setInRequest(false);

        // El snackbar de error ya lo muestra el interceptor de axiosPost.
        if (!respuesta.ok) return;

        setSnackbarInfo({
            message: "Rango horario creado correctamente",
            severity: "success",
            open: true,
        });
        updateList();
        closeModal();
    };

    return (
        <Formik
            initialValues={{
                horarioId: horario?.id ?? "",
                // Solo para mostrarlo en el campo deshabilitado. Va por Formik y no
                // por un `value` suelto: el input se alimenta de `useField`, así que
                // un value pasado por afuera queda a merced del orden del spread.
                horarioNombre: horario?.nombre ?? "",
                entrada: "09:00",
                salida: "18:00",
            }}
            validationSchema={yup.object({
                entrada: yup.string().required(FORM_ERROR_MESSAGES.required),
                salida: yup.string().required(FORM_ERROR_MESSAGES.required),
            })}
            onSubmit={handleSubmit}
        >
            <Form className={styles.inputs} noValidate>
                <div className={styles["column"]}>
                    <Grid
                        container
                        rowSpacing={0}
                        columnSpacing={2}
                        sx={{
                            width: "100%",
                            "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
                        }}
                    >
                        <Grid item xs={12}>
                            <FormikTextInput
                                fullWidth
                                name="horarioNombre"
                                labelText="Horario"
                                readOnly
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SelectorDias
                                value={dias}
                                onChange={(_, nuevos) => setDias(nuevos)}
                                helperText={sinDias ? "Elegí al menos un día" : undefined}
                            />
                        </Grid>
                        {/* Input NATIVO de hora. Lo muestra en 12 o en 24 horas
                            segun el idioma del navegador, y no hay forma de forzarlo:
                            no existe un atributo de formato, y el `lang` del documento
                            —que ya esta en "es"— lo ignora. Se probo el TimePicker de
                            `@mui/lab` y resulto ser un cascaron deprecado que devuelve
                            `null`; los pickers vivos estan en `@mui/x-date-pickers`,
                            que no esta instalado.

                            Lo que SE GUARDA es siempre "HH:mm", muestre lo que muestre. */}
                        <Grid item xs={6}>
                            <FormikTextInput fullWidth name="entrada" labelText="Entrada" type="time" />
                        </Grid>
                        <Grid item xs={6}>
                            <FormikTextInput fullWidth name="salida" labelText="Salida" type="time" />
                        </Grid>
                    </Grid>
                    <FormButton
                        inRequest={inRequest}
                        submitDisabled={sinDias}
                        newFormData={{ button: "Agregar" }}
                        onCloseModal={closeModal}
                    />
                    {inRequest && <SpinnerOverlay />}
                </div>
            </Form>
        </Formik>
    );
}
